import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  RegistrationRequest,
  RegistrationResponse,
  LoginResponse,
  UpdateProfileData,
  UpdateProfileResponse,
  User,
} from "../interfaces/auth.interface";

import { authService } from "../services/auth.service";
import { twoFactorService } from "../services/twoFactor.service";
import { socialService } from "../services/social.service";
import { sessionService } from "../../../services/sessionService";
import { handleRejectError } from "../../../utils/api/api.utils";
import { TokenManager } from "../../../utils/storage/tokenmanager.storage";

export const login = createAsyncThunk<
  LoginResponse,
  { email: string; password: string; rememberMe?: boolean }
>("auth/login", async (credentials, { rejectWithValue, dispatch }) => {
  try {
    const response = await authService.login(credentials);
    if (response.success && response.data) {
      const { accessToken, refreshToken, user } = response.data;
      if (accessToken && refreshToken) {
        TokenManager.setTokens(accessToken, refreshToken);
      }
      TokenManager.setUser(user);

      // Fetch cookie preferences after successful login
      try {
        await dispatch(getCookiePreferencesThunk()).unwrap();
      } catch (cookieError) {
        console.warn('Failed to fetch cookie preferences after login:', cookieError);
      }

      return response.data;
    }
    return rejectWithValue(response.message || "Login failed");
  } catch (error: any) {
    return handleRejectError(rejectWithValue, error);
  }
});

export const createAccount = createAsyncThunk<
  RegistrationResponse,
  RegistrationRequest
>("auth/register", async (data: RegistrationRequest, { rejectWithValue }) => {
  try {
    return await authService.register(data);
  } catch (error) {
    return handleRejectError(rejectWithValue, error);
  }
});

export const verifyEmail = createAsyncThunk(
  "auth/verifyEmail",
  async (
    data: { email: string; verificationCode: string },
    { rejectWithValue },
  ) => {
    try {
      return await authService.verifyEmail(data);
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const resendVerificationCode = createAsyncThunk(
  "auth/resendVerification",
  async (payload: string | { email: string }, { rejectWithValue }) => {
    try {
      const email = typeof payload === "string" ? payload : payload.email;
      return await authService.resendVerificationCode(email);
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// --- Email change ----------------------------------------------------------

export const requestEmailChange = createAsyncThunk(
  "auth/requestEmailChange",
  async (newEmail: string, { rejectWithValue }) => {
    try {
      return await authService.requestEmailChange(newEmail);
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const resendEmailChangeCode = createAsyncThunk(
  "auth/resendEmailChangeCode",
  async (_, { rejectWithValue }) => {
    try {
      return await authService.resendEmailChangeCode();
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const verifyEmailChange = createAsyncThunk(
  "auth/verifyEmailChange",
  async (verificationCode: string, { rejectWithValue }) => {
    try {
      return await authService.verifyEmailChange(verificationCode);
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const socialLogin = createAsyncThunk(
  "auth/socialLogin",
  async (
    { provider, token }: { provider: string; token: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await authService.socialLogin(provider, token);
      if (response.success && response.data) {
        const { accessToken, refreshToken, user } = response.data;
        if (accessToken && refreshToken) {
          TokenManager.setTokens(accessToken, refreshToken);
        }
        if (user) {
          TokenManager.setUser(user);
        }

        try {
          await dispatch(getCookiePreferencesThunk()).unwrap();
        } catch (cookieError) {
          console.warn('Failed to fetch cookie preferences after social login:', cookieError);
        }

        return response.data;
      }
      return response.data;
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const updateProfile = createAsyncThunk<
  UpdateProfileResponse,
  UpdateProfileData
>("auth/profile/update", async (data, { rejectWithValue }) => {
  try {
    const response = await authService.updateProfile(data);
    if (response.success && response.data) {
      if (response.data.user) {
        TokenManager.setUser(response.data.user);
      } else if (response.data) {
        // handle cases where data is the user
        TokenManager.setUser(response.data as any);
      }
      return response.data;
    }
    return rejectWithValue(response.message);
  } catch (error) {
    return handleRejectError(rejectWithValue, error);
  }
});

export const uploadProfileImage = createAsyncThunk(
  "auth/profile/uploadImage",
  async (file: File, { rejectWithValue }) => {
    try {
      const response = await authService.uploadProfileImage(file);
      if (response && response.data) {
        TokenManager.setUser(response.data);
        return response.data;
      }
      return response;
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const updatePreferences = createAsyncThunk(
  "auth/profile/updatePreferences",
  async (preferences: string[], { rejectWithValue }) => {
    try {
      return await authService.updatePreferences(preferences);
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (email: string, { rejectWithValue }) => {
    try {
      return await authService.forgotPassword(email);
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const verifyResetCode = createAsyncThunk(
  "auth/verifyResetCode",
  async (
    { email, code }: { email: string; code: string },
    { rejectWithValue },
  ) => {
    try {
      return await authService.verifyResetCode(email, code);
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async (data: any, { rejectWithValue }) => {
    try {
      return await authService.changePassword(data);
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const verifyCurrentPassword = createAsyncThunk(
  "auth/verifyCurrentPassword",
  async (password: string, { rejectWithValue }) => {
    try {
      return await authService.verifyCurrentPassword(password);
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (data: any, { rejectWithValue }) => {
    try {
      return await authService.resetPassword(data);
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const verifyLoginOTP = createAsyncThunk(
  "auth/verifyLoginOTP",
  async (
    { email, code }: { email: string; code: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await authService.verifyLoginOTP(email, code);
      if (response.success && response.data) {
        const { accessToken, refreshToken, user } = response.data;
        if (accessToken && refreshToken) {
          TokenManager.setTokens(accessToken, refreshToken);
        }
        if (user) {
          TokenManager.setUser(user);
        }

        // Fetch cookie preferences after successful OTP verification
        try {
          await dispatch(getCookiePreferencesThunk()).unwrap();
        } catch (cookieError) {
          console.warn('Failed to fetch cookie preferences after OTP verification:', cookieError);
        }

        return response.data;
      }
      return response.data;
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const getSocialStatus = createAsyncThunk("auth/social/status", async (_, { rejectWithValue }) => {
  try {
    const response = await socialService.getStatus();
    return (response as any)?.data ?? response;
  } catch (error) {
    return handleRejectError(rejectWithValue, error);
  }
});

export const getProfile = createAsyncThunk(
  "auth/getProfile",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await authService.getProfile();
      if (response.success && response.data) {
        TokenManager.setUser(response.data);

        // Fetch cookie preferences after getting profile
        try {
          await dispatch(getCookiePreferencesThunk()).unwrap();
        } catch (cookieError) {
          console.warn('Failed to fetch cookie preferences after profile load:', cookieError);
        }

        return response.data;
      }
      return response.data;
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const toggleVisitorMode = createAsyncThunk(
  "auth/setVisitor",
  async (isVisitor: boolean) => {
    TokenManager.setVisitor(isVisitor);
    return isVisitor;
  }
);

export type DeleteUserAccountPayload = {
  reason: string;
  description?: string;
  action?: 'delete' | 'deactivate';
};

export const deleteUserAccount = createAsyncThunk(
  "auth/deleteAccount",
  async (payload: DeleteUserAccountPayload, { rejectWithValue }) => {
    try {
      const result = await authService.deleteAccount(payload);
      await TokenManager.clearTokens();
      return result;
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      const result = await authService.logout();
      // Only clear tokens on explicit logout
      await TokenManager.clearTokens();
      return result;
    } catch (error) {
      // Logout locally even if server fails (e.g. offline)
      await TokenManager.clearTokens();
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// 2FA Thunks
export const get2FAStatus = createAsyncThunk(
  "auth/2fa/status",
  async (_, { rejectWithValue }) => {
    try {
      const res = await twoFactorService.getStatus();
      return (res as any)?.data ?? res;
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const enable2FA = createAsyncThunk(
  "auth/2fa/enable",
  async (
    data: {
      method: "email" | "phone";
      phoneNumber?: string;
      phoneCode?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await twoFactorService.enable(
        data.method,
        data.phoneNumber,
        data.phoneCode,
      );
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const verify2FACode = createAsyncThunk(
  "auth/2fa/verify",
  async (code: string, { rejectWithValue }) => {
    try {
      return await twoFactorService.verifyCode(code);
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const resend2FACode = createAsyncThunk("auth/2fa/resend", async (_, { rejectWithValue }) => {
  try {
    return await twoFactorService.resendCode();
  } catch (error) {
    return handleRejectError(rejectWithValue, error);
  }
});

export const disable2FA = createAsyncThunk("auth/2fa/disable", async (data: { email: string; password: string }, { rejectWithValue }) => {
  try {
    await twoFactorService.verifyCredentials(data.email, data.password);
    await twoFactorService.disable();
  } catch (error) {
    return handleRejectError(rejectWithValue, error);
  }
});

// Social Account Thunks
export const getSocialAccounts = createAsyncThunk(
  "auth/social/accounts",
  async (_, { rejectWithValue }) => {
    try {
      return await socialService.getAccounts();
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const connectSocialAccount = createAsyncThunk(
  "auth/social/connect",
  async (data: any, { rejectWithValue }) => {
    try {
      return await socialService.connect(
        data.provider,
        data.providerId,
        data.providerEmail,
        data.providerName,
        data.accessToken,
        data.refreshToken,
      );
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);



export const disconnectSocialAccount = createAsyncThunk("auth/social/disconnect", async (provider: string, { rejectWithValue }) => {
  try {
    await socialService.disconnect(provider);
    return provider;
  } catch (error) {
    return handleRejectError(rejectWithValue, error);
  }
});

export const getCookiePreferencesThunk = createAsyncThunk(
  "auth/getCookiePreferences",
  async (_, { rejectWithValue }) => {
    try {
      return await authService.getCookiePreferences();
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const updateCookiePreferences = createAsyncThunk(
  "auth/updateCookiePreferences",
  async (preferences: any, { rejectWithValue }) => {
    try {
      return await authService.updateCookiePreferences(preferences);
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// Session thunks
export const getSessions = createAsyncThunk(
  "auth/sessions/get",
  async (
    params: { page: number; limit: number; append?: boolean } = { page: 1, limit: 15 },
    { rejectWithValue },
  ) => {
    try {
      const { page, limit, append } = params;
      const result = await sessionService.getSessions(page, limit);
      return { ...result, append: !!append };
    } catch (error) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const revokeSession = createAsyncThunk("auth/sessions/revoke", async (sessionId: string, { rejectWithValue }) => {
  try {
    await sessionService.revokeSession(sessionId);
    return sessionId;
  } catch (error) {
    return handleRejectError(rejectWithValue, error);
  }
});

export const revokeAllOtherSessions = createAsyncThunk("auth/sessions/revokeAll", async (_, { rejectWithValue }) => {
  try {
    await sessionService.revokeAllOtherSessions();
  } catch (error) {
    return handleRejectError(rejectWithValue, error);
  }
});
